import {
  SEGMENT_FIELDS,
  getOperatorsForField,
  getValueEditorType,
  DEFAULT_TIME_WINDOW,
  getFieldOptionConfig,
} from "@/constants/segmentOptions";

export const uid = () =>
  crypto.randomUUID?.() ??
  `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const DEFAULT_SEGMENT = {
  name: "",
  description: "",
  definition: {
    all: [],
    any: [],
    none: [],
  },
  cpm: "",
  kind: 15,
  icon: null,
};

export const ensureArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const parseMultiValue = (raw, valueType) => {
  const base = ensureArray(raw);
  if (valueType === "number") {
    return base.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  }
  return base;
};

export const toDisplayMultiValue = (value) => {
  if (!value) return "";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
};

export const normalizeCondition = (condition) => {
  const { id: _id, value, ...rest } = condition;
  if (rest.op === "exists") {
    return rest;
  }

  if (rest.op === "between") {
    const [min, max] = Array.isArray(value) ? value : [];
    return { ...rest, value: [Number(min), Number(max)] };
  }

  if (rest.op === "timeWindow") {
    const schedule = value || DEFAULT_TIME_WINDOW;
    const rules = (schedule.rules || []).map((rule) => ({
      days: Array.isArray(rule.days) ? rule.days : ensureArray(rule.days),
      start: rule.start,
      end: rule.end,
    }));
    return { ...rest, value: { tz: schedule.tz || "UTC", rules } };
  }

  return { ...rest, value };
};

export const definitionListToEditor = (list) =>
  (list || []).map((condition) => ({
    id: uid(),
    ...condition,
  }));

export const mapSegmentToForm = (segment) => {
  if (!segment)
    return {
      ...DEFAULT_SEGMENT,
      definition: { ...DEFAULT_SEGMENT.definition },
    };

  return {
    ...DEFAULT_SEGMENT,
    ...segment,
    definition: {
      all: definitionListToEditor(segment.definition?.all),
      any: definitionListToEditor(segment.definition?.any),
      none: definitionListToEditor(segment.definition?.none),
    },
  };
};

export const createDefaultCondition = (
  fieldValue = "country",
  fieldOptionConfig = null,
) => {
  const fieldDef =
    SEGMENT_FIELDS.find((f) => f.value === fieldValue) || SEGMENT_FIELDS[1];
  const operators = getOperatorsForField(fieldDef.value);
  const defaultOperator = operators[0] || {
    value: "eq",
    types: [fieldDef.type],
  };
  const optionConfig = fieldOptionConfig
    ? getFieldOptionConfig(fieldDef.value, fieldOptionConfig)
    : getFieldOptionConfig(fieldDef.value);
  const editorType = getValueEditorType(defaultOperator);
  let value;

  if (optionConfig) {
    if (editorType === "multi" || optionConfig.type === "multi") {
      value = [];
    } else {
      const firstOption = optionConfig.options[0];
      value = firstOption
        ? optionConfig.toRaw(firstOption.value)
        : fieldDef.type === "number"
          ? 0
          : "";
    }
  } else {
    switch (editorType) {
      case "range":
        value = [0, 0];
        break;
      case "timeWindow":
        value = DEFAULT_TIME_WINDOW;
        break;
      case "none":
        value = undefined;
        break;
      case "multi":
        value = [];
        break;
      default:
        value = fieldDef.type === "number" ? 0 : "";
    }
  }

  return {
    id: uid(),
    field: fieldDef.value,
    op: defaultOperator.value,
    value,
    ci: fieldDef.type === "string" ? true : undefined,
    disabled: false,
  };
};
